import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Claude connector access (client profile card)
//
// People listed here can add the Core6 Hub connector to their OWN Claude
// account and see this one client's data, with a reduced tool set (no
// personas, Other documents, insights report, or keyword follow-ups). The MCP
// server (functions/mcp/access.js) matches their verified WorkOS sign-in email
// against mcpClientAccess/{email}.
//
// Entries are turned off, never deleted (firestore.rules forbids deletes), so
// there is always a record of who had access. Anyone not on this list and not
// in MCP_STAFF_EMAILS (functions/.env) is refused by the server. Only the owner
// can grant or change access; other staff see the list read-only.
// ---------------------------------------------------------------------------

const CONNECTOR_URL = 'https://hub.core6.marketing/mcp';
const OWNER_EMAIL = 'cmgsalinas@gmail.com';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fmtDate(ts) {
  const d = ts?.toDate ? ts.toDate() : null;
  if (!d) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ active }) {
  // Text label + filled vs outlined shape so status never depends on hue alone.
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      ...(active
        ? { background: 'var(--text-primary)', color: 'var(--bg-primary, #fff)', border: '1px solid var(--text-primary)' }
        : { background: 'transparent', color: 'var(--text-muted)', border: '1px dashed var(--text-muted)' }),
    }}>
      {active ? '● Active' : '○ Off'}
    </span>
  );
}

export default function ClaudeAccessPanel({ clientId, client }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(false);
  const isOwner = auth.currentUser?.email === OWNER_EMAIL;

  useEffect(() => {
    const q = query(collection(db, 'mcpClientAccess'), where('clientId', '==', clientId));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.email || a.id).localeCompare(b.email || b.id));
      setEntries(data);
    }, () => setEntries([]));
    return () => unsub();
  }, [clientId]);

  const activeCount = entries.filter(e => e.status === 'active').length;

  const addEmail = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) { setMsg({ ok: false, text: 'Enter a valid email address.' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const ref = doc(db, 'mcpClientAccess', clean);
      const existing = await getDoc(ref);
      if (existing.exists() && existing.data().clientId !== clientId) {
        setMsg({ ok: false, text: `${clean} is already linked to ${existing.data().clientName || 'another client'}. One email can only see one client.` });
        return;
      }
      if (existing.exists()) {
        await updateDoc(ref, { status: 'active', updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || null });
        setMsg({ ok: true, text: `Turned access back on for ${clean}.` });
      } else {
        await setDoc(ref, {
          email: clean,
          clientId,
          clientName: client?.businessName || '',
          status: 'active',
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.email || null,
        });
        setMsg({ ok: true, text: `Added ${clean}. Send them the connector URL below.` });
      }
      setEmail('');
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Could not save.' });
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (entry, status) => {
    if (status === 'revoked' && !window.confirm(`Turn off Claude access for ${entry.email || entry.id}?`)) return;
    try {
      await updateDoc(doc(db, 'mcpClientAccess', entry.id), {
        status, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || null,
      });
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Could not update.' });
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the URL is visible to copy by hand */ }
  };

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Claude Connector Access</div>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
            {activeCount ? `${activeCount} active` : 'none'}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
            People listed here can connect their own Claude account to <strong>{client?.businessName || 'this client'}</strong>'s
            data only: brand kit, calls, rankings, forms, reviews, service pages, and the marketing report. Personas,
            Other documents, the insights report, and keyword follow-ups stay staff-only. They sign in with the exact email below.
          </div>

          <div className="form-field">
            <label>Connector URL (Claude → Settings → Connectors → Add custom connector)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" readOnly value={CONNECTOR_URL} style={{ fontFamily: 'monospace', fontSize: 12, flex: 1 }} />
              <button type="button" className="btn btn-secondary btn-sm" onClick={copyUrl}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
          </div>

          {entries.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>No one has client access yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {entries.map(entry => {
                const active = entry.status === 'active';
                const lastUsed = fmtDate(entry.lastUsedAt);
                return (
                  <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <StatusBadge active={active} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.email || entry.id}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {lastUsed ? `Last used ${lastUsed}` : 'Not connected yet'}
                      </div>
                    </div>
                    {isOwner && (
                      active
                        ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus(entry, 'revoked')}>Turn off</button>
                        : <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus(entry, 'active')}>Turn on</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {isOwner ? (
            <form onSubmit={addEmail} style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                placeholder="client@theirbusiness.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !email.trim()}>
                {busy ? 'Saving…' : 'Add'}
              </button>
            </form>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Only Phil can grant or change client access.</div>
          )}

          {msg && (
            <div style={{ fontSize: 12, marginTop: 10, fontWeight: 600, color: msg.ok ? 'var(--text-primary)' : 'var(--accent-red, #c0392b)' }}>
              {msg.ok ? '✓ ' : '✕ '}{msg.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
